import { fetchJaneConfig } from 'modals/JaneDetailsModal/JaneDetailsModal.constants';
import { getJaneColorByStatus } from 'modals/JaneDetailsModal/getJaneColorByStatus';
import { CUSTOMER_TEAMS, AGENCY_TEAMS } from 'constants/endpoints';
import { useGQLQuery } from 'hooks';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ActivityEventEnum, Jane, JaneModalJaneDetailsQuery } from 'types/graphql';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { createGQLDataContext } from 'utils/createDataContext';
import { METHOD_GET, METHOD_PATCH, METHOD_POST } from 'requests';
import { useUserTypeSwitch } from 'hooks/useUserTypeSwitch';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { ApiRequestConfigType } from 'requests/index.types';
import { PaginationInfo } from 'pages/CommunicationPage/DirectPage/DirectPage.types';
import { useTranslation } from 'react-i18next';
import { toastService } from 'services/toastService';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { AGENCY_JANES, B2B_JANES, RESEND_INVITE } from 'constants/endpoints';
import { queryRefetchService } from 'services/QueryRefetchService';

interface Activity {
  id: number;
  event: ActivityEventEnum;
  created_at: string;
  feedable: {
    shift_ids: number[];
  };
}

type JaneModalJaneDetails = JaneModalJaneDetailsQuery['fetchJane'] &
  Pick<Jane, 'shiftEmailEnabled' | 'shiftSmsEnabled' | 'emailAvailable' | 'smsAvailable'>;

interface IFetchJaneByIdData {
  activitiesMeta: PaginationInfo;
  data: JaneModalJaneDetails | undefined;
  dotColor: string;
  refetch: () => void;
  showDeactivateConfirmModal: boolean;
  setShowDeactivateConfirmModal: (show: boolean) => void;
  deactivateTeamMember: () => void;
  reactivateTeamMember: () => void;
  resendInviteTeamMember: () => void;
  fetchNextPageActivities: () => void;
  isLoading: boolean;
  activities: Activity[];
  showPendingSignUpModal: boolean;
  setShowPendingSignUpModal: (show: boolean) => void;
}

export const FetchJaneByIdByIdDataContext = createGQLDataContext<IFetchJaneByIdData>(
  {} as IFetchJaneByIdData,
);

export const FetchJaneByIdByIdDataProvider: FC<{
  janeId: string;
}> = ({ children, janeId }) => {
  const [showDeactivateConfirmModal, setShowDeactivateConfirmModal] = useState(false);
  const [showPendingSignUpModal, setShowPendingSignUpModal] = useState(false);
  const [activities, setActivities] = useState([] as Activity[]);
  const [activitiesMeta, setActivitiesMeta] = useState({} as PaginationInfo);
  const { t } = useTranslation();
  const [isActivitiesCalled, setIsActivitiesCalled] = useState(false);
  const { apiRequest } = useAPIActions();
  const {
    data: value,
    fetch,
    isLoading,
    called,
    refetch,
  } = useGQLQuery<JaneModalJaneDetailsQuery, JaneModalJaneDetailsQuery, { id: string | undefined }>(
    fetchJaneConfig(janeId),
  );

  useEffect(() => {
    if (!called) fetch();
  }, [fetch, called, janeId]);

  const data = value?.fetchJane as JaneModalJaneDetails | undefined;

  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const activitiesApiConfig: ApiRequestConfigType = useUserTypeSwitch({
    B2B: {
      __typename: 'LegacyAPI',
      method: METHOD_GET,
      url: `${CUSTOMER_TEAMS()}/${janeId}/activities`,
      query: {
        with_community: {
          id: currentlySelectedLocation?.value,
          type: 'Community',
        },
      },
    },
    Agency: {
      __typename: 'LegacyAPI',
      method: METHOD_GET,
      url: `${AGENCY_TEAMS()}/${janeId}/activities`,
      query: {
        with_community: {
          id: currentlySelectedLocation?.value,
          type: 'AgencyCommunity',
        },
      },
    },
  });

  const getActivitiesTeamMember = useCallback(
    (pageNumber?: number) =>
      apiRequest(
        {
          ...activitiesApiConfig,
          query: {
            ...activitiesApiConfig.query,
            per_page: 10,
            page: pageNumber ?? 1,
          },
        },
        {
          onSuccess: (response) => {
            const newActivities = response?.data?.activities || [];
            setActivities((prevActivities) => {
              const mergedActivities = [
                ...prevActivities,
                ...newActivities.filter(
                  (newActivity: Activity) =>
                    !prevActivities.some(
                      (existingActivity) => existingActivity.id === newActivity.id,
                    ),
                ),
              ];
              return mergedActivities;
            });
            setActivitiesMeta(response?.data?.meta);
          },
        },
      ),
    [apiRequest, activitiesApiConfig],
  );

  useEffect(() => {
    if (!isActivitiesCalled) {
      setIsActivitiesCalled(true);
      getActivitiesTeamMember();
    }
  }, [isLoading, getActivitiesTeamMember, isActivitiesCalled]);

  const dotColor = getJaneColorByStatus(data?.accessStatus?.name);

  const teamMemberEndPointUrl = useUserTypeSwitch<string>({
    Agency: AGENCY_JANES(janeId),
    B2B: B2B_JANES(janeId),
  });

  const deactivateTeamMember = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_PATCH,
          url: teamMemberEndPointUrl,
          body: { access_status: 'blocked' },
        },
        {
          onSuccess: () => {
            toastService.success(t('success:TEAM_MEMBER_DEACTIVATED'));
            setShowDeactivateConfirmModal(false);
            fetch();
            queryRefetchService.refetchDataQueryByKey('TEAM_MEMBERS_PAGE_JANE_ROW', `${data?.id}`);
          },
        },
      ),
    [apiRequest, teamMemberEndPointUrl, t, fetch, data?.id],
  );

  const reactivateTeamMember = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_PATCH,
          url: teamMemberEndPointUrl,
          body: { access_status: 'approved' },
        },
        {
          onSuccess: () => {
            toastService.success(t('success:TEAM_MEMBER_REACTIVATED'));
            fetch();
            queryRefetchService.refetchDataQueryByKey('TEAM_MEMBERS_PAGE_JANE_ROW', `${data?.id}`);
          },
        },
      ),
    [apiRequest, teamMemberEndPointUrl, t, fetch, data?.id],
  );

  const resendInviteTeamMember = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_POST,
          url: RESEND_INVITE(janeId),
        },
        {
          onSuccess: () => {
            toastService.success(t('success:INVITE_RESENT'));
            fetch();
          },
          onError: handleGenericErrorMessage,
        },
      ),
    [apiRequest, janeId, fetch, t],
  );

  const contextValues = useMemo(() => {
    return {
      activitiesMeta,
      data,
      dotColor,
      refetch,
      showDeactivateConfirmModal,
      setShowDeactivateConfirmModal,
      deactivateTeamMember,
      reactivateTeamMember,
      resendInviteTeamMember,
      fetchNextPageActivities: () => {
        getActivitiesTeamMember((activitiesMeta?.current_page ?? 1) + 1);
      },
      isLoading,
      activities,
      showPendingSignUpModal,
      setShowPendingSignUpModal,
    };
  }, [
    activitiesMeta,
    data,
    dotColor,
    refetch,
    showDeactivateConfirmModal,
    setShowDeactivateConfirmModal,
    deactivateTeamMember,
    reactivateTeamMember,
    resendInviteTeamMember,
    isLoading,
    activities,
    showPendingSignUpModal,
    setShowPendingSignUpModal,
    getActivitiesTeamMember,
  ]);
  return (
    <FetchJaneByIdByIdDataContext.Provider value={contextValues}>
      {children}
    </FetchJaneByIdByIdDataContext.Provider>
  );
};

export const useFetchJaneByIdDataContext = () => useContext(FetchJaneByIdByIdDataContext);
